/**
 * FilingScheduleAdminSummary
 *
 * Self-contained panel rendered inside the State Report Due Dates expand row.
 * Hits GET /FilingScheduleAdmin/detail (Phase 3 endpoint) and shows:
 *   - lifecycle banners: pending period-type, pending deactivation,
 *     reporting effective date that is still in the future, disable-zero-report
 *   - per-mapping "card" with Action, Status, Due Date, Period Type, Time
 *     Period, Tax Amount, Pay To, URL, truncated Instructions, and per-field
 *     "required" flags. Two cards render side-by-side on >= md viewports
 *     (Premium-Bearing | Zero Report); single column on smaller screens.
 *
 * Intentionally read-only. The Phase 4 edit modal will consume the same
 * endpoint to hydrate its form state.
 *
 * Defaults baked in (tell me to change them if MISC uses different copy):
 *   ACTION_LABELS   — actionType column → human-readable string
 *   deriveTimePeriodLabel — periodType + period number → display label
 *                            (Annual → "January - December", Quarterly → "Q{n}", etc.)
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Link,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import moment from 'moment';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import { scheduleFlagToBoolean, calculateTimePeriodLabel, resolveTimePeriodLabel } from '../../models/filingScheduleAdminTypes';

export interface FilingScheduleAdminMapping {
    stateReportTypeMappingId: number;
    filingSchedulesId: number;
    typeOfReport: string | null;
    taxAmount: string | null;
    paymentType: string | null;
    payTo: string | null;
    address: string | null;
    url: string | null;
    instructions: string | null;
    additionalNotes: string | null;
    largeReport: boolean | null;
    reportInvoiceRequired: string | null;
    reconciliationListRequired: string | null;
    actionType: 'notice' | 'no_action' | string;
    sameAsPremium: boolean | null;
    taxRequired: boolean | null;
    payToRequired: boolean | null;
    addressRequired: boolean | null;
    urlRequired: boolean | null;
    instructionsRequired: boolean | null;
    additionalNotesRequired?: boolean | number | string | null;
    /** Zero-report rows: 1 = disabled (Inactive). Premium-bearing is typically 0 (Active). */
    isDisabled?: boolean | number | string | null;
}

export interface FilingScheduleAdminScheduleHeader {
    scheduleId: number;
    stateId: number;
    stateName: string | null;
    scheduleName: string | null;
    periodType: string | null;
    payment: boolean | null;
    report: boolean | null;
    paymentType?: string | null;
    wetSignature?: boolean | null;
    notaryStamp?: boolean | null;
    zeroIndividual?: boolean | null;
    zeroEntity?: boolean | null;
    endOfMonth?: boolean | null;
    setDay?: number | null;
    /** 1-12; Annual schedules only (e.g. Arkansas due June 1). NULL for other period types. */
    setMonth?: number | null;
    notes?: string | null;
    disableZeroReport: boolean | null;
    reportingEffectiveDate: string | null;
    deactivationEffectiveDate: string | null;
    periodTypePending: string | null;
    periodTypeEffectiveDate: string | null;
    isDeleted: boolean | null;
}

export interface FilingScheduleAdminPeriod {
    periodId: number;
    scheduleId: number;
    dueDate: string | null;
    period: number | null;
    timePeriod: string | null;
    skip: boolean | null;
}

export interface AdminDetailResponse {
    schedule: FilingScheduleAdminScheduleHeader | null;
    periods: FilingScheduleAdminPeriod[];
    mappings: FilingScheduleAdminMapping[];
}

interface Iprops {
    scheduleId: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REQUIRED_FIELD_LABELS: Array<{
    key: keyof Pick<
        FilingScheduleAdminMapping,
        'taxRequired' | 'payToRequired' | 'addressRequired' | 'urlRequired' | 'instructionsRequired'
    >;
    label: string;
}> = [
    { key: 'taxRequired', label: 'Tax' },
    { key: 'payToRequired', label: 'Pay To' },
    { key: 'addressRequired', label: 'Address' },
    { key: 'urlRequired', label: 'URL' },
    { key: 'instructionsRequired', label: 'Instructions' },
];

// actionType column -> displayed copy in the summary card. Mirrors the
// sentinel strings backfilled by backfill_StateReportTypeMapping_ActionType.sql.
const ACTION_LABELS: Record<string, string> = {
    notice: 'Notice of State Report',
    no_action: 'No Client Action Required',
};

const EM_DASH = '\u2014';
const INSTRUCTIONS_PREVIEW_CHARS = 60;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (raw: string | null) => {
    if (!raw) return null;
    const m = moment.utc(raw);
    return m.isValid() ? m.format('MM/DD/YYYY') : null;
};

const isFutureDate = (raw: string | null) => {
    if (!raw) return false;
    const m = moment.utc(raw).startOf('day');
    if (!m.isValid()) return false;
    return m.isAfter(moment.utc().startOf('day'));
};

const formatActionLabel = (raw: string | null | undefined) => {
    if (!raw) return EM_DASH;
    return ACTION_LABELS[raw] || raw;
};

// Treats common "no value" sentinels ('', 'N/A', null) as missing so the
// summary renders an em-dash instead of operationally useless strings.
const displayOrDash = (raw: string | null | undefined) => {
    if (raw == null) return EM_DASH;
    const trimmed = String(raw).trim();
    if (trimmed === '' || trimmed.toUpperCase() === 'N/A') return EM_DASH;
    return trimmed;
};

// Earliest due date >= today, falling back to the most recent past period.
// "Primary" because annual schedules often store two rows (one for this fiscal
// year, one for next) and the user wants to see what's next, not what's stale.
const selectPrimaryPeriod = (periods: FilingScheduleAdminPeriod[]): FilingScheduleAdminPeriod | null => {
    if (!periods?.length) return null;
    const today = moment.utc().startOf('day');

    const withDates = periods.filter((p) => p?.dueDate && moment.utc(p.dueDate).isValid());
    if (withDates.length === 0) return periods[0] ?? null;

    const future = withDates.filter((p) => moment.utc(p.dueDate as string).isSameOrAfter(today));
    if (future.length > 0) {
        return future.reduce((min, p) =>
            moment.utc(p.dueDate as string).isBefore(moment.utc(min.dueDate as string)) ? p : min,
            future[0],
        );
    }

    return withDates.reduce((max, p) =>
        moment.utc(p.dueDate as string).isAfter(moment.utc(max.dueDate as string)) ? p : max,
        withDates[0],
    );
};

// Derives a friendly "Time Period" label. Falls back to the raw timePeriod
// string when periodType isn't recognized — prototype's calculateTimePeriod()
// can be ported here later if MISC wants different labels.
const deriveTimePeriodLabel = (
    periodType: string | null | undefined,
    period: number | null | undefined,
    rawTimePeriod: string | null | undefined,
): string => {
    if (!periodType) return rawTimePeriod || EM_DASH;
    const lower = periodType.toLowerCase();
    const periodNum = period ?? 1;

    if (lower.includes('semi') && lower.includes('annual')) {
        return resolveTimePeriodLabel('Semi-Annual', periodNum, rawTimePeriod);
    }
    if (lower.includes('annual')) {
        return resolveTimePeriodLabel('Annual', periodNum, rawTimePeriod);
    }
    if (lower.includes('quarter')) {
        return resolveTimePeriodLabel('Quarterly', periodNum, rawTimePeriod);
    }
    if (lower.includes('month')) {
        return resolveTimePeriodLabel('Monthly', periodNum, rawTimePeriod);
    }

    return rawTimePeriod || EM_DASH;
};

const truncate = (s: string | null | undefined, max: number) => {
    if (s == null) return EM_DASH;
    const trimmed = String(s).trim();
    if (trimmed === '') return EM_DASH;
    return trimmed.length > max ? `${trimmed.substring(0, max).trim()}\u2026` : trimmed;
};

const isMeaningfulUrl = (raw: string | null | undefined) => {
    if (!raw) return false;
    const trimmed = String(raw).trim();
    if (trimmed === '' || trimmed.toUpperCase() === 'N/A') return false;
    return /^https?:\/\//i.test(trimmed);
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const labelSx = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    color: '#64748b',
    pt: '2px',
};

const valueSx = {
    fontSize: 13,
    color: '#0f172a',
    lineHeight: 1.4,
    wordBreak: 'break-word' as const,
};

interface FieldRowProps {
    label: string;
    children: React.ReactNode;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, children }) => (
    <>
        <Typography sx={labelSx}>{label}</Typography>
        <Box sx={{ minWidth: 0 }}>{children}</Box>
    </>
);

interface MappingCardProps {
    mapping: FilingScheduleAdminMapping;
    schedule: FilingScheduleAdminScheduleHeader;
    primaryPeriod: FilingScheduleAdminPeriod | null;
}

const MappingCard: React.FC<MappingCardProps> = ({ mapping, schedule, primaryPeriod }) => {
    const requiredFlags = REQUIRED_FIELD_LABELS.filter((f) =>
        scheduleFlagToBoolean(mapping[f.key]),
    );
    const sameAsPremium = scheduleFlagToBoolean(mapping.sameAsPremium);

    // IsDisabled on mapping (0 = Active, 1 = Inactive); schedule soft-delete also shows Inactive.
    const isActive = !schedule.isDeleted && !scheduleFlagToBoolean(mapping.isDisabled);
    const statusColor = isActive ? '#15803d' : '#475569';
    const statusBg = isActive ? '#dcfce7' : '#f1f5f9';

    const dueDateLabel = formatDate(primaryPeriod?.dueDate ?? null) ?? EM_DASH;
    const timePeriodLabel = deriveTimePeriodLabel(
        schedule.periodType,
        primaryPeriod?.period ?? null,
        primaryPeriod?.timePeriod ?? null,
    );

    const fullInstructions = mapping.instructions || '';
    const instructionPreview = truncate(mapping.instructions, INSTRUCTIONS_PREVIEW_CHARS);

    return (
        <Box
            sx={{
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                p: 1.5,
            }}
        >
            <Typography
                sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#64748b',
                    mb: 1,
                    textAlign: 'left',
                }}
            >
                {mapping.typeOfReport || '(untitled mapping)'}
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '90px 1fr',
                    columnGap: 1.5,
                    rowGap: 0.75,
                    alignItems: 'baseline',
                    textAlign: 'left',
                }}
            >
                <FieldRow label="Action">
                    <Typography sx={valueSx}>{formatActionLabel(mapping.actionType)}</Typography>
                </FieldRow>

                <FieldRow label="Status">
                    <Chip
                        size="small"
                        label={isActive ? 'Active' : 'Inactive'}
                        sx={{
                            backgroundColor: statusBg,
                            color: statusColor,
                            fontWeight: 600,
                            fontSize: 11,
                            height: 22,
                        }}
                    />
                </FieldRow>

                <FieldRow label="Due date">
                    <Typography sx={valueSx}>{dueDateLabel}</Typography>
                </FieldRow>

                <FieldRow label="Period type">
                    <Typography sx={valueSx}>{schedule.periodType || EM_DASH}</Typography>
                </FieldRow>

                <FieldRow label="Time period">
                    <Typography sx={valueSx}>{timePeriodLabel}</Typography>
                </FieldRow>

                <FieldRow label="Tax amount">
                    <Typography sx={valueSx}>{displayOrDash(mapping.taxAmount)}</Typography>
                </FieldRow>

                <FieldRow label="Pay to">
                    <Typography sx={valueSx}>{displayOrDash(mapping.payTo)}</Typography>
                </FieldRow>

                <FieldRow label="URL">
                    {isMeaningfulUrl(mapping.url) ? (
                        <Link
                            href={mapping.url as string}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                ...valueSx,
                                color: '#2563eb',
                                wordBreak: 'break-all',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            {mapping.url}
                        </Link>
                    ) : (
                        <Typography sx={valueSx}>{EM_DASH}</Typography>
                    )}
                </FieldRow>

                <FieldRow label="Instructions">
                    {fullInstructions ? (
                        <Tooltip title={fullInstructions} placement="bottom-start" arrow>
                            <Typography
                                sx={{
                                    ...valueSx,
                                    cursor: 'help',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {instructionPreview}
                            </Typography>
                        </Tooltip>
                    ) : (
                        <Typography sx={valueSx}>{EM_DASH}</Typography>
                    )}
                </FieldRow>

                <FieldRow label="Flags">
                    {requiredFlags.length === 0 ? (
                        <Typography sx={{ ...valueSx, color: '#94a3b8', fontStyle: 'italic' }}>
                            No flags
                        </Typography>
                    ) : (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {sameAsPremium && (
                                <Chip
                                    size="small"
                                    label="Same as premium"
                                    variant="outlined"
                                    sx={{ fontSize: 11, height: 22 }}
                                />
                            )}
                            {requiredFlags.map((f) => (
                                <Chip
                                    key={f.key}
                                    size="small"
                                    label={`${f.label} required`}
                                    variant="outlined"
                                    sx={{ fontSize: 11, height: 22 }}
                                />
                            ))}
                        </Stack>
                    )}
                </FieldRow>
            </Box>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const FilingScheduleAdminSummary: React.FC<Iprops> = ({ scheduleId }) => {
    const { user } = useAuth();
    const token = user?.token;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<AdminDetailResponse | null>(null);

    useEffect(() => {
        if (!scheduleId || !token) return;
        let cancelled = false;
        setLoading(true);
        setError(null);

        apiClient(`/FilingScheduleAdmin/detail?scheduleId=${scheduleId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
        })
            .then((res) => {
                if (cancelled) return;
                if (!res || res.status === false) {
                    setError(res?.message || 'Failed to load filing schedule admin detail.');
                    return;
                }
                setData(res.data as AdminDetailResponse);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.message || 'Unexpected error loading admin detail.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [scheduleId, token]);

    const banners = useMemo(() => {
        const out: Array<{ severity: 'info' | 'warning'; text: string }> = [];
        if (!data?.schedule) return out;
        const s = data.schedule;

        if (isFutureDate(s.reportingEffectiveDate)) {
            out.push({
                severity: 'info',
                text: `Reporting rules take effect on ${formatDate(s.reportingEffectiveDate)}. Current behavior is unchanged until then.`,
            });
        }

        if (s.periodTypePending && isFutureDate(s.periodTypeEffectiveDate)) {
            out.push({
                severity: 'warning',
                text: `Period type change pending: switching to "${s.periodTypePending}" on ${formatDate(s.periodTypeEffectiveDate)}.`,
            });
        }

        if (isFutureDate(s.deactivationEffectiveDate)) {
            out.push({
                severity: 'warning',
                text: `Scheduled deactivation on ${formatDate(s.deactivationEffectiveDate)}. Outstanding queue rows on or after this date will be removed.`,
            });
        }

        if (s.disableZeroReport) {
            out.push({
                severity: 'info',
                text: 'Zero reports are disabled for this schedule. Generation will skip zero-only filings.',
            });
        }

        return out;
    }, [data]);

    const primaryPeriod = useMemo(
        () => selectPrimaryPeriod(data?.periods ?? []),
        [data?.periods],
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, color: '#64748b' }}>
                <CircularProgress size={14} />
                <Typography variant="caption">Loading admin detail…</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mx: 0, my: 1 }}>
                {error}
            </Alert>
        );
    }

    if (!data?.schedule) {
        return null;
    }

    return (
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #e2e8f0' }}>
            {banners.length > 0 && (
                <Stack spacing={0.5} sx={{ mb: 1 }}>
                    {banners.map((b, i) => (
                        <Alert key={i} severity={b.severity} sx={{ py: 0, px: 1, fontSize: 12 }}>
                            {b.text}
                        </Alert>
                    ))}
                </Stack>
            )}

            <Typography
                sx={{
                    fontWeight: 600,
                    fontSize: '12px',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: '#64748b',
                    display: 'block',
                    mb: 0.75,
                }}
            >
                State Report Type Mapping
            </Typography>

            {data.mappings.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                    No mapping rows configured.
                </Typography>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                        gap: 1.5,
                    }}
                >
                    {data.mappings.map((m) => (
                        <MappingCard
                            key={m.stateReportTypeMappingId}
                            mapping={m}
                            schedule={data.schedule!}
                            primaryPeriod={primaryPeriod}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};
